import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "config/routes";

const getClientId = (client) =>
  client?.id ||
  client?._id ||
  client?.clientId ||
  client?.client?.id ||
  client?.client?._id ||
  null;

const ClientTable = ({ clients, onEdit, onDelete, onViewLedger }) => {
  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Client
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Contact
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Email
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Tax ID
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Balance
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {clients.map((client, index) => (
              <ClientRow
                key={getClientId(client) || `${client.email || client.name}-${index}`}
                client={client}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
                onViewLedger={onViewLedger}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ClientRow = ({ client, index, onEdit, onDelete, onViewLedger }) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const clientId = getClientId(client);

  const actions = [
    {
      icon: "Edit3",
      label: "Edit Client",
      color: "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20",
      onClick: () => onEdit(client),
    },
    {
      icon: "FilePlus",
      label: "Add Invoice",
      color: "text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20",
      onClick: () =>
        navigate(ROUTES.INVOICE_CREATE, {
          state: { prefillCustomer: client },
        }),
    },
    {
      icon: "FileText",
      label: "View Invoices",
      color: "text-primary hover:bg-primary/10",
      onClick: () =>
        navigate(ROUTES.INVOICE_HUB, {
          state: { filterClient: clientId },
        }),
    },
    {
      icon: "BookOpen",
      label: "View Ledger",
      color: "text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20",
      onClick: () => onViewLedger(client),
    },
    {
      icon: "Clock",
      label: "Payment History",
      color: "text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20",
      onClick: () => onViewLedger(client),
    },
    {
      icon: "Trash2",
      label: "Delete Client",
      color: "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20",
      onClick: () => setShowDeleteConfirm(true),
    },
  ];

  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const avatarColors = [
    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
    "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
    "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  ];
  const avatarColor = avatarColors[index % avatarColors.length];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        {/* Client Name */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${avatarColor}`}
            >
              {getInitials(client.name)}
            </div>
            <div>
              <div className="font-medium text-foreground">
                {client.salutation ? `${client.salutation} ` : ""}
                {client.name}
              </div>
              {client.additionalDetail && (
                <div className="text-xs text-muted-foreground truncate max-w-[160px]">
                  {client.additionalDetail}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Contact */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-2 text-foreground">
            <Icon
              name="Phone"
              size={14}
              className="text-muted-foreground flex-shrink-0"
            />
            <span className="text-sm">{client.contact || "—"}</span>
          </div>
        </td>

        {/* Email */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <Icon
              name="Mail"
              size={14}
              className="text-muted-foreground flex-shrink-0"
            />
            <span className="text-sm text-foreground truncate max-w-[180px]">
              {client.email || "—"}
            </span>
          </div>
        </td>

        {/* Tax ID */}
        <td className="px-6 py-4">
          <span className="text-sm text-foreground font-mono">
            {client.vatNumber || client.taxId || "—"}
          </span>
        </td>

        {/* Balance */}
        <td className="px-6 py-4">
          {client.openingBalance ? (
            <div>
              <span
                className={`text-sm font-semibold ${
                  client.balanceType === "owes_you"
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-500 dark:text-red-400"
                }`}
              >
                {client.balanceType === "owes_you" ? "+" : "-"}SR{" "}
                {parseFloat(client.openingBalance).toLocaleString("en-SA", {
                  minimumFractionDigits: 2,
                })}
              </span>
              <div className="text-xs text-muted-foreground">
                {client.balanceType === "owes_you"
                  ? "Client owes you"
                  : "You owe client"}
              </div>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">No balance</span>
          )}
        </td>

        {/* Actions */}
        <td className="px-6 py-4">
          <div className="flex items-center gap-1">
            {actions.map((action) => (
              <ActionIconButton
                key={action.label}
                icon={action.icon}
                label={action.label}
                colorClass={action.color}
                onClick={action.onClick}
              />
            ))}
          </div>
        </td>
      </motion.tr>

      {/* Delete Confirmation Row */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td
              colSpan={6}
              className="px-6 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Are you sure you want to delete <strong>{client.name}</strong>
                  ? This cannot be undone.
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onDelete(clientId);
                      setShowDeleteConfirm(false);
                    }}
                    disabled={!clientId}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

const ActionIconButton = ({ icon, label, colorClass, onClick }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      className={`p-1.5 rounded-lg transition-colors ${colorClass}`}
      title={label}
    >
      <Icon name={icon} size={15} />
    </button>
    {/* Tooltip */}
    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

export default ClientTable;
